import React, { FC } from "react"
import { Dropdown, DropdownButton } from "react-bootstrap"
import { useDispatch, useSelector } from "react-redux"

import { useModal } from "@/helpers/useModal"
import {
  canEdit,
  canEditActiveBid,
  getActiveBid,
  getProduct,
  getFeatures,
  getProductFilters,
  isBidUnderReview,
} from "@/onboarding/features/product/selectors"
import {
  acceptBid,
  closeProductForBidding,
  openProductForBidding,
  pendBid,
  removeBid,
  resetProduct,
  returnProductToSetup,
  submitBid,
} from "@/onboarding/features/product/Slice"
import routes from "@/routes"
import ButtonLink from "@/components/ButtonLink"

import CruiseLineOnly from "@/auth_descriptor/CruiseLineOnly"
import ApplyToToursModal from "@/onboarding/features/product/components/shared/apply_to_tours/ApplyToToursModal"
import FieldSettingsModal from "./FieldSettingsModal"
import AddContractPeriodModal from "./AddContractPeriodModal"
import ImportComponentModal from "./ImportComponentModal"
import ImportTourModal from "./ImportTourModal"
import SetInternalTourTagModal from "./SetInternalTourTagModal"
import TourChangeLogModal from "./TourChangeLogModal"

const ToolsTitle: FC = () => (
  <>
    <i className="fas fa-cog me-2" />
    Tools
  </>
)

// TODO Move the bid related stuff into the app as well, so that this can function completely using
//  the feature checks.
const ToolsDropdown: FC = () => {
  const dispatch = useDispatch()

  const {
    id,
    season: { id: seasonId },
  } = useSelector(getProduct)
  const { contractPeriodId } = useSelector(getProductFilters)
  const activeBid = useSelector(getActiveBid)
  const {
    toolsDropdown: features,
    general: {
      product: { applyToTours: showApplyToTours },
    },
  } = useSelector(getFeatures)

  // Same visibility as the per-section import icons (SectionHolder): the
  // owning operator's editable bid, or an editable view outside bid review.
  const editableBid = useSelector(canEditActiveBid)
  const canEditView = useSelector(canEdit)
  const underReview = useSelector(isBidUnderReview)
  const showImportTour = editableBid || (canEditView && !underReview)

  const toggleSetInternalTourTagModal = useModal((props) => <SetInternalTourTagModal {...props} />)
  const toggleFieldSettingsModal = useModal((props) => <FieldSettingsModal {...props} />)
  const toggleAddContractPeriodModal = useModal((props) => <AddContractPeriodModal {...props} />)
  const toggleImportComponentModal = useModal((props) => <ImportComponentModal {...props} />)
  const toggleImportTourModal = useModal((props) => <ImportTourModal {...props} />)
  const toggleApplyToToursModal = useModal((props) => <ApplyToToursModal {...props} />)
  const toggleChangeLogModal = useModal((props) => <TourChangeLogModal {...props} productId={id} />)

  if (!_.some(features, (enabled) => enabled) && !showImportTour) {
    return null
  }

  return (
    <div>
      <DropdownButton title={<ToolsTitle />}>
        {features.submitBid && activeBid && (
          <Dropdown.Item onClick={() => dispatch(submitBid({ bidId: activeBid.id }))}>
            Submit Bid
          </Dropdown.Item>
        )}
        {features.submitProposal && (
          <ButtonLink
            label="Propose Tour"
            className="dropdown-item"
            button={false}
            modal
            href={routes.new_onboarding_tour_marketplace_product_proposal_management_path({
              product_id: id,
            })}
          />
        )}
        {features.openBidding && (
          <Dropdown.Item onClick={() => dispatch(openProductForBidding({}))}>
            Open Bidding
          </Dropdown.Item>
        )}
        {features.closeBidding && (
          <Dropdown.Item onClick={() => dispatch(closeProductForBidding({}))}>
            Close Bidding
          </Dropdown.Item>
        )}
        {features.returnToSetup && (
          <Dropdown.Item onClick={() => dispatch(returnProductToSetup({}))}>
            Return to Pending
          </Dropdown.Item>
        )}
        {features.editMainDetails && (
          <ButtonLink
            label="Edit Main Details"
            className="dropdown-item"
            button={false}
            modal
            href={routes.new_onboarding_edit_main_detail_path({ id })}
          />
        )}
        <CruiseLineOnly>
          {features.modifyFieldSettings && (
            <Dropdown.Item onClick={toggleFieldSettingsModal}>Edit Field Settings</Dropdown.Item>
          )}
        </CruiseLineOnly>
        {features.costAnalysis && (
          <Dropdown.Item href={routes.onboarding_product_cost_analysis_path({ id })}>
            Cost Analysis
          </Dropdown.Item>
        )}
        {features.inventoryAllocation && (
          <Dropdown.Item
            href={routes.onboarding_inventory_allocation_path({
              season_id: seasonId,
              product_id: id,
            })}
          >
            Inventory Allocation
          </Dropdown.Item>
        )}
        {features.acceptBid && activeBid && (
          <Dropdown.Item onClick={() => dispatch(acceptBid({ bidId: activeBid.id }))}>
            Accept Bid
          </Dropdown.Item>
        )}
        {features.rejectBid && activeBid && (
          <ButtonLink
            label="Reject Bid"
            className="dropdown-item"
            button={false}
            modal
            params={{
              season_id: seasonId,
              product_ids: [id],
              operator_id: activeBid.operator.id,
            }}
            href={routes.new_onboarding_bidding_deny_appointment_path()}
          />
        )}
        {features.rerequestBid && activeBid && (
          <Dropdown.Item onClick={() => dispatch(pendBid({ bidId: activeBid.id }))}>
            Request Bid Resubmission
          </Dropdown.Item>
        )}
        {features.removeBid && activeBid && (
          <Dropdown.Item onClick={() => dispatch(removeBid({ bidId: activeBid.id }))}>
            Remove Bid
          </Dropdown.Item>
        )}
        {features.reset && (
          <Dropdown.Item onClick={() => dispatch(resetProduct({}))}>Reset Tour</Dropdown.Item>
        )}
        {features.addContractPeriod && (
          <Dropdown.Item onClick={toggleAddContractPeriodModal}>Adjust Rates</Dropdown.Item>
        )}
        {features.requestContractPeriodDetailsConfirmation && contractPeriodId && (
          <ButtonLink
            label="Request Tour Details Confirmation"
            className="dropdown-item"
            button={false}
            modal
            href={routes.new_onboarding_bidding_request_contract_period_details_confirmation_path({
              season_id: seasonId,
              contract_period_id: contractPeriodId,
            })}
          />
        )}
        {features.suggestContractPeriodDetailsConfirmation && contractPeriodId && (
          <ButtonLink
            label="Suggest Tour Changes"
            className="dropdown-item"
            button={false}
            modal
            href={routes.new_onboarding_bidding_suggest_contract_period_details_confirmation_path({
              season_id: seasonId,
              contract_period_id: contractPeriodId,
            })}
          />
        )}
        {features.cruiseLineSuggestContractPeriodDetailsConfirmation && contractPeriodId && (
          <ButtonLink
            label="Suggest Tour Detail Changes"
            className="dropdown-item"
            button={false}
            modal
            href={routes.new_onboarding_bidding_cruise_line_suggest_contract_period_details_confirmation_path(
              {
                season_id: seasonId,
                contract_period_id: contractPeriodId,
              }
            )}
          />
        )}
        {features.importComponent && (
          <Dropdown.Item onClick={toggleImportComponentModal}>Import Component</Dropdown.Item>
        )}
        {showImportTour && (
          <Dropdown.Item onClick={toggleImportTourModal}>Import from Tour</Dropdown.Item>
        )}
        {showApplyToTours && (
          <Dropdown.Item onClick={toggleApplyToToursModal}>Apply to Tour/s</Dropdown.Item>
        )}
        {features.lockComponentEditing && (
          <ButtonLink
            label="Lock Component Editing"
            className="dropdown-item"
            button={false}
            modal
            href={routes.new_onboarding_bidding_lock_product_component_editing_path({
              product_id: id,
            })}
          />
        )}
        {features.unlockComponentEditing && (
          <ButtonLink
            label="Unlock Component Editing"
            className="dropdown-item"
            button={false}
            modal
            href={routes.new_onboarding_bidding_unlock_product_component_editing_path({
              product_id: id,
            })}
          />
        )}
        {features.updateRatesheetTypeLock && (
          <ButtonLink
            label="Ratesheet Type Lock"
            className="dropdown-item"
            button={false}
            href={routes.new_onboarding_bidding_update_ratesheet_type_lock_path({
              season_id: seasonId,
              preview_tour_id: id,
            })}
          />
        )}
        {features.otalioExport && (
          <ButtonLink
            label="Export to Otalio"
            className="dropdown-item"
            button={false}
            modal
            params={{ product_ids: [id] }}
            href={routes.new_data_exchange_exports_otalio_product_export_path()}
          />
        )}
        {features.otalioExportSettings && (
          <ButtonLink
            label="Configure Otalio Auto-Export"
            className="dropdown-item"
            button={false}
            modal
            params={{ product_id: id }}
            href={routes.new_data_exchange_exports_otalio_export_setting_path()}
          />
        )}
        {features.downloadCsv && (
          <Dropdown.Item href={routes.download_onboarding_tour_library_path({ id })}>
            Download Tour CSV
          </Dropdown.Item>
        )}
        <CruiseLineOnly>
          <Dropdown.Item onClick={toggleSetInternalTourTagModal}>
            Update Internal Tour Status
          </Dropdown.Item>
        </CruiseLineOnly>
        <Dropdown.Item onClick={toggleChangeLogModal}>Tour Change Log</Dropdown.Item>
      </DropdownButton>
    </div>
  )
}

export default ToolsDropdown
